import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse, makeStyles } from '@material-ui/core';
import {
  Checkbox,
  FormControlLabel,
  ThemeProvider,
  Typography,
} from '@knockrentals/knock-shared-web-v2';
import PropertyList from './PropertyList';

const useStyles = makeStyles({
  section: {
    marginBottom: '1rem',
  },
  enableRow: {
    marginBottom: '0.5rem',
  },
  description: {
    marginLeft: '2rem',
    marginTop: '-0.25rem',
  },
});

const LinkedProperties = ({
  fetchNearbyProperties,
  onDoorwayChanged,
  propertyId,
  settings,
  teamProperties,
}) => {
  const classes = useStyles();
  const preferences = settings.preferences || {};
  const isEnabled = preferences.multi_phase_enabled || false;
  const [hasFetchError, setHasFetchError] = useState(false);

  useEffect(() => {
    const fetchTeamProperties = async () => {
      try {
        await fetchNearbyProperties(propertyId);
      } catch {
        setHasFetchError(true);
      }
    };

    if (isEnabled) {
      fetchTeamProperties();
    }
  }, [isEnabled, propertyId, fetchNearbyProperties]);

  const handleEnableLinkedPropertiesOnChange = (event) => {
    const isEnabled = event.target.checked;

    const updatedPreferences = {
      ...preferences,
      multi_phase_enabled: isEnabled,
    };

    if (!isEnabled) {
      updatedPreferences.linked_property_ids = [];
      setHasFetchError(false);
    }

    const updatedSettings = {
      ...settings,
      preferences: updatedPreferences,
    };

    onDoorwayChanged(propertyId, updatedSettings);
  };

  const handleLinkedPropertyOnChange = (selectedPropertyId) => (e) => {
    const { checked: isSelected } = e.target;
    const currentIds = preferences.linked_property_ids || [];

    const newLinkedPropertyIds = isSelected
      ? [...currentIds, selectedPropertyId]
      : currentIds.filter((id) => id !== selectedPropertyId);

    const updatedSettings = {
      ...settings,
      preferences: {
        ...preferences,
        linked_property_ids: newLinkedPropertyIds,
      },
    };

    onDoorwayChanged(propertyId, updatedSettings);
  };

  return (
    <ThemeProvider>
      <div className={classes.section}>
        <h3>Linked Properties</h3>

        <div className={classes.enableRow}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isEnabled}
                color="default"
                onChange={handleEnableLinkedPropertiesOnChange}
              />
            }
            label="Enable Multi-Phased Property setup"
          />
          <Typography
            className={classes.description}
            color="secondary.500"
            variant="body2"
          >
            Enable feature to merge property details into a single Knockbot
            experience for prospects.
          </Typography>
        </div>

        {hasFetchError && (
          <Typography color="error.400" variant="body2">
            We are unable to manage linked properties at this time. Please try again later.
          </Typography>
        )}

        <Collapse in={isEnabled && !hasFetchError} mountOnEnter unmountOnExit>
          <PropertyList
            handleLinkedPropertyOnChange={handleLinkedPropertyOnChange}
            selectedPropertyIds={preferences.linked_property_ids || []}
            teamProperties={teamProperties}
          />
        </Collapse>
      </div>
    </ThemeProvider>
  );
};

LinkedProperties.propTypes = {
  fetchNearbyProperties: PropTypes.func.isRequired,
  onDoorwayChanged: PropTypes.func.isRequired,
  propertyId: PropTypes.number.isRequired,
  settings: PropTypes.shape({
    preferences: PropTypes.shape({
      multi_phase_enabled: PropTypes.bool,
      linked_property_ids: PropTypes.arrayOf(PropTypes.number),
    }),
  }).isRequired,
  teamProperties: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      distance: PropTypes.number,
    }),
  ),
};

export default LinkedProperties;
